import axios from 'axios';
import { ActionContext } from 'vuex';
import QueryString from 'qs';

import Router from '@/main/router';

import { checkoutBySlug, getUpsellCart, checkoutPaymentBySlug, checkoutEarlyRenew, getTermsOfUse, getPolicy } from '@/api/resources/checkout';

import { IApp } from '@/interfaces/App';
import { IRewardsSummary } from '@/interfaces/Rewards';
import { ICheckout, ICheckoutCartOfferAvailability, ICheckoutCartOfferCondition, ICheckoutCartOfferEligibility, ICheckoutCartOfferPayment, ICheckoutCartOfferRelated } from '@/interfaces/Checkout';

import Cookie from '@/helpers/cookie';
import { pushCheckoutLoadedToDatalayer, pushCheckoutDataToDatalayer } from '@/helpers/dataLayer';
import { applyAvailability, applyEligibility, treatCheckoutError } from '@/helpers/checkout';
import { EErrorExhibition, EErrorType, EErrorSeverity, EPaymentMethod, EPaymentMethodTypes } from '@/helpers/enums';
import { addError, getErrorByCodeAndType, getDefaultErrorByType } from '@/helpers/errors';
import { sendInteractEvent } from '@/helpers/events';
import { EInteractEventTrackerAction } from '@/app/features/InteractEventTracker/enums/EInteractEventTracker';

import TraceableError from '@/classes/TraceableError';
import { Checkout } from '@/classes/Checkout/Checkout';
import { Offer } from '@/classes/Checkout/Offer';

import { getOrderFormByGuid } from '@/api/resources/square';

export default {
  toggleLoader({ commit, getters }: ActionContext<any, any>, loading: boolean) {
    const isUserLogged = getters['Customer/isLogged'];

    if (isUserLogged) {
      commit('setPageLoading', loading);
    } else {
      commit('Checkout/setOfferLoading', loading);
    }
  },
  toggleLoaderCard({ commit, getters }: ActionContext<any, any>, loading: boolean) {
    const isUserLogged = getters['Customer/isLogged'];

    if (isUserLogged) {
      commit('setCardSelectedLoading', loading);
      commit('Checkout/setOfferLoading', loading);
    }
  },
  toogleLoaderAmount({ commit, getters }: ActionContext<any, any>, loading: boolean) {
    const isUserLogged = getters['Customer/isLogged'];
    if (isUserLogged) {
      commit('Checkout/setOfferLoading', loading);
      commit('setAmountLoading', loading);
    }
  },
  async prepareCheckoutEverywhere({ commit, dispatch, getters }:
  ActionContext<IApp, any>, { slug, payload, enableHiddenMethods, params }: { slug: string, payload: any, enableHiddenMethods?: boolean, params?: any }) {
    try {
      let requestMethod = checkoutBySlug;

      if (getters['Customer/isLogged']) {
        requestMethod = checkoutPaymentBySlug;
      }

      const request = await requestMethod(slug, payload, enableHiddenMethods, params);
      const checkout = request.data;

      return checkout;
    } catch (error) {
      return error;
    }
  },
  async prepareCheckout({ commit, dispatch, getters }: ActionContext<IApp, any>, { slug, payload, enableHiddenMethods }: { slug: string, payload: any, enableHiddenMethods?: boolean }) {
    if (!slug) return;

    const isRenewRoute = getters['Checkout/isRenewal'] || Router.currentRoute.name === 'renew';
    const cardFirstDigits = payload && payload.card_first_digits || null;
    const paymentMethod = payload && payload.payment_method || null;

    const uuid = await dispatch('App/Behavior/setUUID', '', { root: true });

    const customPaymentParams = payload && payload.custom_payment_parameters;

    if (process.env.VUE_APP_AB_TESTING === 'true') {
      const segment = getters['Behavior/ABSegmentByUUID'](uuid);

      if (segment && segment.id !== process.env.VUE_APP_AB_TESTING_SEGMENT) {
        window.location.replace(`${segment.url}${Router.currentRoute.fullPath}`);
      }
    }

    try {
      if (customPaymentParams) {
        dispatch('toogleLoaderAmount', true);
      } else if (cardFirstDigits || paymentMethod !== EPaymentMethodTypes.creditCard) {
        dispatch('toggleLoaderCard', true);
      } else {
        dispatch('toggleLoader', true);
      }

      let requestMethod = checkoutBySlug;

      if (getters['Customer/isLogged']) {
        requestMethod = checkoutPaymentBySlug;

        if (isRenewRoute) {
          requestMethod = checkoutEarlyRenew;
        }
      }

      const request = await requestMethod(slug, payload, enableHiddenMethods);

      const checkout: ICheckout = new Checkout(request.data);

      const { custom_template_guid: templateGuid } = checkout.cart;

      const hasRewards = checkout.cart.offer.payment_structure && checkout.cart.offer.payment_structure.some((paymentStructure: ICheckoutCartOfferPayment) => {
        return paymentStructure.payment_conditions && paymentStructure.payment_conditions.some((condition: ICheckoutCartOfferCondition) => {
          return condition.payment_method_code === EPaymentMethod.empiricusRewards;
        });
      });

      if (hasRewards) {
        if (getters['Customer/isLogged']) {
          try {
            await dispatch('Customer/getRewardsSummary');
          } catch (error) {
            console.warn(error);
          }
        }
      }

      if (templateGuid && !isRenewRoute) {
        const template = await getOrderFormByGuid(templateGuid);
        const orderFormURL = `${process.env.VUE_APP_ORDERFORM_BASE_URL}${templateGuid}`;

        const { data: manifest } = await axios.get(`${orderFormURL}/manifest.json`);

        let styles: string[] = [];

        if (manifest) {
          const { stylesheets = [], scripts = [] } = manifest;

          if (stylesheets.length) {
            const requests = stylesheets.map((url: string) => axios.get(`${orderFormURL}/${url}`));
            styles = await Promise.all(requests);
          }

          if (scripts.length) {
            commit('Checkout/setOrderFormScripts', scripts.map((url: string) => `${orderFormURL}/${url}`));
          }
        }

        commit('Checkout/setOrderFormTemplate', template);
        commit('Checkout/setOrderFormStyles', styles.map((style: any) => style.data));
      }

      const applyCheckoutRulesToChoosePaymentStructure = (checkout: ICheckout): Checkout => {
        if (checkout && Offer.offerIsValidToBuy(checkout.cart.offer)) {
          const selectedPriceId = Number(new Cookie({ name: 'COOKIE_EMP_PRICE_SELECTED' }).getSession().value);

          const customerRewardsSummary: IRewardsSummary = getters['Customer/rewardsSummary'];

          const checkoutWithPrice = new Checkout(checkout, {
            priceId: selectedPriceId,
            rewards: customerRewardsSummary,
          });

          const canBuySomeOfferPriceWithRewards = Offer.canBuySomeOfferPriceUsingRewardsPointsToo(
            checkoutWithPrice.cart.offer,
            customerRewardsSummary
          );

          if (!canBuySomeOfferPriceWithRewards) {
            checkoutWithPrice.cart.offer.selectOfferInitialPrice(true);

            dispatch('App/Behavior/disablePay', '', { root: true });
          }

          return checkoutWithPrice;
        }

        return checkout;
      };

      if (checkout.cart) {
        const offerHash = `${slug}?${QueryString.stringify(Router.currentRoute.query)}`;

        const getBestChoiceId = (): Number|null => {
          const relatedOffers = checkout.cart.offer.related_offers as ICheckoutCartOfferRelated[];

          if(!relatedOffers || !relatedOffers.length) return null;

          const bestChoice = relatedOffers.find((offer: any) => offer.is_best_choice);

          return bestChoice && bestChoice.id || null;
        };

        const selectedOfferIdCookie = Number(new Cookie({ name: 'COOKIE_EMP_OFFER_SELECTED' }).getSession().value);
        const selectedOfferId = selectedOfferIdCookie || getBestChoiceId();
        const selectedPaymentMethod = new Cookie({ name: 'COOKIE_EMP_PAYMENT_METHOD_SELECTED' }).getSession().value;

        let checkoutToApply = applyCheckoutRulesToChoosePaymentStructure(checkout);

        commit('Checkout/setCheckout', checkoutToApply);

        selectedOfferId && commit('Checkout/selectOffer', selectedOfferId);
        selectedPaymentMethod && commit('Checkout/selectOfferPaymentMethod', { offer_id: selectedOfferId, method: selectedPaymentMethod });

        pushCheckoutDataToDatalayer(checkoutToApply);

        if (!getters['Behavior/isOfferAlreadyLoaded'](offerHash)) {
          dispatch('App/Behavior/setOfferLoaded', offerHash, { root: true });

          pushCheckoutLoadedToDatalayer(checkout);
        }

        if (checkout.cart.offer && checkout.cart.offer.display_name) {
          document.title = checkout.cart.offer.display_name;
        }
      }

      const availability: ICheckoutCartOfferAvailability = getters['Checkout/selectedOfferAvailability'];
      const eligibility: ICheckoutCartOfferEligibility = getters['Checkout/selectedOfferEligibility'];

      if (checkout.cart.offer.coupon) {
        const errorCode = checkout.cart.offer.coupon.error_code;

        if (errorCode) {
          sendInteractEvent({
            action: EInteractEventTrackerAction.CHK_CUPOM_FAILED,
            attributes: {
              return_code: errorCode,
              return_message: 'Invalid coupon.',
            }
          });
        } else {
          sendInteractEvent({
            action: EInteractEventTrackerAction.CHK_CUPOM_SUCCESS,
            attributes: {
              return_code: 200,
              return_message: 'OK',
            }
          });
        }
      }

      if (availability) {
        const appliedAvailability: any = applyAvailability(
          availability,
          availability.error_code,
        );

        if (appliedAvailability.willRedirect) return appliedAvailability.redirectCallback(() => {
          commit('Checkout/reRenderCheckout');
        });

        if (!appliedAvailability.isAvailable && availability.error_code) {
          const error = getErrorByCodeAndType(availability.error_code, [EErrorType.availability]);

          if (error) {
            throw new TraceableError({
              ...error,
              type: EErrorType.availability,
              trigger: 'applyAvailability',
              exhibition: EErrorExhibition.modal,
            });
          }
        }
      }

      if (eligibility && getters['Customer/isLogged']) {
        const appliedEligibility = await applyEligibility(
          eligibility,
          eligibility.error_code,
        );

        const isAddressRequired = getters['Checkout/isAddressRequired'];

        if (appliedEligibility.isEligible) {
          let checkoutToApply = applyCheckoutRulesToChoosePaymentStructure(appliedEligibility.checkout);

          checkoutToApply && commit('Checkout/setCheckout', checkoutToApply);

          if (checkout.cart && isAddressRequired && getters['Customer/isLogged']) {
            await dispatch('Customer/getAddresses');
          }

          appliedEligibility.redirectCallback();

          dispatch('toggleLoaderCard', false);

          return dispatch('toggleLoader', false);
        }
      }

      if (!Offer.offerIsValidToBuy(checkout.cart.offer)) {
        const traceableError = getDefaultErrorByType(EErrorType.loading);

        throw new TraceableError({
          ...traceableError,
          type: EErrorType.loading,
          code: 'offerWithoutConditions',
          trigger: 'prepareCheckout',
          severity: EErrorSeverity.critical,
          exhibition: EErrorExhibition.modal,
        });
      }

      if (checkout.cart.offer) {
        const errorCode = checkout!.cart!.offer!.availability
          ? checkout!.cart!.offer!.availability!.error_code
          : checkout!.cart!.offer!.eligibility
            ? checkout!.cart!.offer!.eligibility.error_code
            : false;

        if (errorCode) {
          const traceableError = getDefaultErrorByType(EErrorType.loading);

          throw new TraceableError({
            ...traceableError,
            type: EErrorType.loading,
            code: errorCode,
            trigger: 'prepareCheckout',
            severity: EErrorSeverity.warning,
            exhibition: EErrorExhibition.modal,
          });
        }
      }

      dispatch('toggleLoaderCard', false);
      dispatch('toggleLoader', false);

      return checkout;
    } catch (error) {
      treatCheckoutError(error);

      if (error.exhibition && error.exhibition === EErrorExhibition.alert) {
        dispatch('toggleLoaderCard', false);
        dispatch('toggleLoader', false);
      }

      if (error.exhibition && error.exhibition === EErrorExhibition.modal) {
        dispatch('toggleLoaderCard', false);
        dispatch('toggleLoader', false);
      }
    } finally {
      dispatch('toogleLoaderAmount', false);
    }
  },
  async prepareUpsell({ dispatch, getters, commit }: ActionContext<any, any>, { upsellId, orderId, params }: { upsellId: any, orderId: any, params?: any }) {
    try {
      if (params && params.custom_payment_parameters) {
        dispatch('toogleLoaderAmount', true);
      }
      if (params) {
        dispatch('toggleLoaderCard', true);
      } else {
        dispatch('Checkout/setPaymentLoading', true);
      }

      let payload = {};

      payload = {
        ...(params && {
          params: {
            ...params,
          }
        }),
        ...{
          uriParams: {
            upsellId,
            orderId,
          }
        }
      };

      const request: any = await getUpsellCart(payload);
      const checkout: ICheckout = new Checkout(request.data);

      const hasRewards = checkout.cart.offer.payment_structure && checkout.cart.offer.payment_structure.some((paymentStructure: ICheckoutCartOfferPayment) => {
        return paymentStructure.payment_conditions && paymentStructure.payment_conditions.some((condition: ICheckoutCartOfferCondition) => {
          return condition.payment_method_code === EPaymentMethod.empiricusRewards;
        });
      });

      if (hasRewards) {
        if (getters['Customer/isLogged']) {
          try {
            await dispatch('Customer/getRewardsSummary');
          } catch (error) {
            console.warn(error);
          }
        }
      }

      const selectedPriceId = Number(new Cookie({ name: 'COOKIE_EMP_PRICE_SELECTED' }).getSession().value);
      const customerRewardsSummary: IRewardsSummary = getters['Customer/rewardsSummary'];

      const checkoutWithPrice = new Checkout(checkout, {
        priceId: selectedPriceId,
        rewards: customerRewardsSummary,
      });

      const canBuySomeOfferPriceWithRewards = Offer.canBuySomeOfferPriceUsingRewardsPointsToo(
        checkoutWithPrice.cart.offer,
        customerRewardsSummary
      );

      if (!canBuySomeOfferPriceWithRewards) {
        checkoutWithPrice.cart.offer.selectOfferInitialPrice(true);

        await dispatch('App/Behavior/disablePay', '', { root: true });
      }

      commit('Checkout/setCheckout', checkoutWithPrice);
      dispatch('Checkout/setPaymentLoading', false);
    } catch (error) {
      if (error.response) {
        const traceableError = getDefaultErrorByType(EErrorType.loading);

        addError(new TraceableError({
          ...traceableError,
          type: EErrorType.loading,
          code: error.response.status,
          trigger: 'prepareUpsell',
          severity: EErrorSeverity.critical,
          exhibition: EErrorExhibition.none,
        }));
      }

    } finally {
      dispatch('toggleLoaderCard', false);
      dispatch('toogleLoaderAmount', false);
    }
  },
  async getTermsOfUse({ commit }: ActionContext<any, any>) {
    commit('requestTermsOfUse');

    try {
      const request = await getTermsOfUse();
      const termsOfUse = request.data.data.text;

      commit('successTermsOfUse', termsOfUse);
    } catch (error) {
      commit('failureTermsOfUse');

      if (error.response) {
        const traceableError = getDefaultErrorByType(EErrorType.loadingTerms);

        addError(new TraceableError({
          ...traceableError,
          type: EErrorType.loadingTerms,
          code: error.response.status,
          trigger: 'getTermsOfUse',
          severity: EErrorSeverity.critical,
          exhibition: EErrorExhibition.none,
        }));
      }
    }
  },
  async getPolicy({ commit }: ActionContext<any, any>) {
    commit('requestPolicy');

    try {
      const request = await getPolicy();
      const policy = request.data.data.text;

      commit('successPolicy', policy);
    } catch (error) {
      commit('failurePolicy');

      if (error.response) {
        const traceableError = getDefaultErrorByType(EErrorType.loadingTerms);

        addError(new TraceableError({
          ...traceableError,
          type: EErrorType.loadingTerms,
          code: error.response.status,
          trigger: 'getPolicy',
          severity: EErrorSeverity.critical,
          exhibition: EErrorExhibition.none,
        }));
      }
    }
  },
};
